import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Clause, PatternBased, PatternBranch, PatternMeasure, RuleAction, RuleBased, RuleBranch, RuleSet } from '@kkp-fraud/shared';
import type { ApiSettings } from '../api';
import {
  branchesOf,
  copyMeasure,
  deriveRequires,
  isExpressionRow,
  needsPatternBranches,
  patternBranchesOf,
  patternRowRule,
  withBranches,
  withPatternBranches,
} from '../branches';
import {
  ACTIONS,
  AGGREGATES,
  EVENTS,
  FIELD_GROUPS,
  FIELD_KINDS,
  PRIORITIES,
  SCOPES,
  isHistoryField,
  type FieldName,
} from '../fields';
import { formatWindow } from '../format';
// Wave 7 long windows
import {
  DAY_SECONDS,
  LONG_OFF_AGGREGATES,
  MAX_WINDOW_DAYS,
  UNIT_LABEL,
  WINDOW_PRESETS,
  WINDOW_UNITS,
  isLongWindow,
  windowDays,
  windowInputValue,
  windowUnitFor,
  windowChoices,
  UNIT_DEFAULT,
  presetUnit,
  type WindowUnit,
} from '../longWindows';
import {
  EXPR_AGGREGATES,
  EXPR_SCOPES,
  FIELD_AGGREGATES,
  HISTORY_ONLY_AGGREGATES,
  MAX_EXPR_MEASURES,
  NUMERIC_AGGREGATES,
  expressionRowText,
  measureNames,
  nextMeasureName,
  renameInConditions,
  toExpressionRow,
  toThresholdRow,
} from '../expressions';
import { ExprConditions } from './ExprConditions';
import { t, useLocale } from '../i18n';
import { fieldsUsedBy } from '../sentence';
import { fieldsForSources, sendsField, type SourceId } from '../sources';
import { useFeederConfig } from '../hooks/useFeederConfig';
import { useCustomFields } from '../hooks/useCustomFields';
import { useMasterLists } from '../hooks/useMasterLists';
import { upsertRuleIn, upsertedIndex } from '../hooks/useRuleSetDraft';
import { useRuleCheck } from '../hooks/useRuleCheck';
import { DEMO } from '../demo';
import { ROW_CONFLICT_CODES, chainProblems, fieldPath, problemText, problemsOfRule, rowOf, withoutMirrorRepeats, type CheckedProblem } from '../ruleCheck';
import { CheckButton, CheckStatusLine, ProblemList } from './RuleCheckView';
import { BlocksWorkspace } from '../blocks/BlocksWorkspace';
import { ACTION_CLASS } from './Chips';
import { ClauseBuilder } from './ClauseBuilder';
import { InlineDryRun } from './InlineDryRun';
import { Tip } from './PageHead';
import { ConfirmDialog, Dialog } from './Dialog';
import { offeredFor, sourceIntersection } from './SourceButtons';
// 2c rule sets
import { useRuleSets } from '../hooks/useRuleSets';
import { SET_FIRED, liveSets, setLabel } from '../ruleSets';

export type EditorTarget =
  | { kind: 'ruleBased'; rule: RuleBased; isNew: boolean }
  | { kind: 'patternBased'; rule: PatternBased; isNew: boolean };

export interface RuleEditorProps {
  target: EditorTarget;
  /**
   * `previousId` is the id the rule had when the editor opened — it differs from `rule.id`
   * when the id was renamed, and the caller replaces that rule in place.
   */
  onSave: (kind: 'ruleBased' | 'patternBased', rule: RuleBased | PatternBased, previousId: string) => void;
  onDelete?: (kind: 'ruleBased' | 'patternBased', id: string) => void;
  onClose: () => void;
  /** Where the in-editor dry run posts. */
  settings: ApiSettings;
  /** The current draft — the dry run drops the edited rule into it. `null` disables the dry run. */
  ruleSet: RuleSet | null;
  /**
   * An account that may only read (a Viewer): every field is shown disabled, the canvases are
   * read-only, and there is no "+ action" / "+ aggregate" / "+ condition" / ✕ / Delete / Apply.
   * The dry run stays — it writes nothing.
   */
  readOnly?: boolean;
}

/** A new action row: the same starting condition a new rule gets. */
const NEW_BRANCH: RuleBranch = { action: 'APPROVE', clauses: [{ field: 'amount_thb', op: 'gt', value: 0 }] };

/**
 * The field a new action row's one starting condition (`field > 0`) is about: a number field no
 * row of the rule uses yet (`amount_thb` first), so the new row can neither repeat a row above it
 * nor be hidden by one — it opens without a problem to fix (QA-R-12). `fraud_score` is left out:
 * it would make the whole rule depend on the Mastercard score. `null` when every one is taken.
 */
function freshRowField(rows: readonly RuleBranch[], allowed: readonly FieldName[]): FieldName | null {
  const used = new Set(rows.flatMap((row) => (row.clauses ?? []).map((c) => String(c.field))));
  const numbers = allowed.filter((f) => FIELD_KINDS[f] === 'number' && f !== 'fraud_score' && !used.has(f));
  return numbers.includes('amount_thb') ? 'amount_thb' : (numbers[0] ?? null);
}

/**
 * A copy of a pattern row's first measure that no row counts yet: the same count over another
 * window (twice as long, or a shorter one when that would pass 24 h). Its own window makes it a
 * different measure, so the new row neither repeats row 1 nor sits under it (QA-R-12). A
 * calendar measure ("this hour" / "today") switches to the other calendar instead. A long
 * window (Wave 7, over 24 h) stays in whole days — 2 to 366 of them — or falls back to 24 h.
 */
export function freshMeasure(first: PatternMeasure, rows: readonly PatternBranch[]): PatternMeasure {
  const copy = copyMeasure(first);
  if (copy.calendar !== undefined) {
    const calendar = copy.calendar === 'hour' ? 'day' : 'hour';
    return { ...copy, calendar, windowSeconds: calendar === 'hour' ? 3600 : 86400 };
  }
  const taken = new Set(rows.flatMap((row) => row.measures.map((m) => m.windowSeconds)));
  const base = Number.isFinite(first.windowSeconds) && first.windowSeconds > 0 ? first.windowSeconds : 600;
  if (isLongWindow(base)) {
    const days = Math.max(2, Math.round(base / DAY_SECONDS));
    const choices = [2, 3, 4, 6, 12].map((f) => days * f).filter((d) => d <= MAX_WINDOW_DAYS);
    for (let d = Math.floor(days / 2); d >= 2; d = Math.floor(d / 2)) choices.push(d);
    const free = choices.map((d) => d * DAY_SECONDS).find((w) => !taken.has(w));
    return { ...copy, windowSeconds: free ?? (taken.has(DAY_SECONDS) ? days * DAY_SECONDS : DAY_SECONDS) };
  }
  const candidates = [2, 3, 4, 6, 12, 24].map((f) => base * f).filter((w) => w <= 86400);
  for (let w = Math.floor(base / 2); w >= 1; w = Math.floor(w / 2)) candidates.push(w);
  const windowSeconds = candidates.find((w) => !taken.has(w)) ?? base;
  return { ...copy, windowSeconds };
}

/**
 * `rowSources` key: a condition chain and a condition row in it. A chain is an action row
 * (`"0"`) for a rule-based rule and an action row's measure (`"0.1"`) for a pattern rule.
 */
const rowKey = (chain: string, row: number) => `${chain}:${row}`;

/**
 * Source choices after a row or measure is removed: chains under `prefix` whose next index
 * is `removed` are dropped, and those after it move up one — so the choices stay with the
 * conditions they were made for. Chains elsewhere are kept as they are.
 */
function shiftChains(prev: Record<string, string[]>, prefix: number[], removed: number): Record<string, string[]> {
  const next: Record<string, string[]> = {};
  for (const [key, value] of Object.entries(prev)) {
    const [chain = '', row = ''] = key.split(':');
    const parts = chain.split('.').map(Number);
    const under = prefix.every((p, i) => parts[i] === p);
    const at = parts[prefix.length];
    if (!under || at === undefined || at < removed) next[key] = value;
    else if (at > removed) next[`${[...prefix, at - 1, ...parts.slice(prefix.length + 1)].join('.')}:${row}`] = value;
  }
  return next;
}

/**
 * The rule as the editor opens it: a deep copy, exactly as stored. Every action row keeps its
 * own structure — the engine only asks the rule level to mirror row 1 (docs/engine-rows-semantics.md §5).
 */
function openRule(target: EditorTarget): RuleBased | PatternBased {
  return JSON.parse(JSON.stringify(target.rule)) as RuleBased | PatternBased;
}

/** What Apply saves: row 1 mirrored at the rule level, `requires` derived. */
function savedForm(kind: 'ruleBased' | 'patternBased', rule: RuleBased | PatternBased): RuleBased | PatternBased {
  return kind === 'ruleBased'
    ? deriveRequires(withBranches(rule as RuleBased, branchesOf(rule as RuleBased)))
    : withPatternBranches(rule as PatternBased, patternBranchesOf(rule as PatternBased));
}

/** A Viewer's canvas writes nothing back. */
const ignoreCanvas = () => {};

/** 2a expressions — problems the engine reports at an expression row itself (`branches[k]`). */
const EXPRESSION_ROW_CODES: ReadonlySet<string> = new Set(['logic_with_conditions', 'conditions_required', 'too_many_measures', 'measures_required']);

/** 2a expressions — the label of an aggregate: the older three from `agg.*`, the Wave 2a ones from `expr.agg.*`. */
const aggregateLabel = (a: string) => (a === 'count' || a === 'sum' || a === 'distinct' ? t(`agg.${a}.label`) : t(`expr.agg.${a}`));

export function RuleEditor({ target, onSave, onDelete, onClose, settings, ruleSet, readOnly = false }: RuleEditorProps) {
  const [kind] = useState(target.kind);
  const [rule, setRuleState] = useState<RuleBased | PatternBased>(() => openRule(target));
  /**
   * The rule as the latest edit left it, readable in the same event handler that made the edit —
   * Apply and close first flush a canvas edit still waiting on its debounce (QA-R-14), and the
   * state it writes is only visible at the next render. Every write goes through `setRule`.
   */
  const ruleRef = useRef(rule);
  const setRule = useCallback((next: RuleBased | PatternBased | ((prev: RuleBased | PatternBased) => RuleBased | PatternBased)) => {
    const value = typeof next === 'function' ? next(ruleRef.current) : next;
    ruleRef.current = value;
    setRuleState(value);
  }, []);
  /** A question on top of the editor: throw the edits away, or delete the rule. */
  const [ask, setAsk] = useState<'discard' | 'delete' | null>(null);
  /** Each canvas's "push what you show now" (`BlocksWorkspace` `registerFlush`). */
  const flushers = useRef(new Set<() => boolean>());
  const registerFlush = useCallback((flush: () => boolean) => {
    flushers.current.add(flush);
    return () => {
      flushers.current.delete(flush);
    };
  }, []);
  /** Every canvas writes its pending edit into the rule now; true when one did. */
  const flushCanvases = () => {
    let changed = false;
    for (const flush of flushers.current) changed = flush() || changed;
    return changed;
  };
  /**
   * Which sources each condition row is switched on for.
   *
   * Editor-local on purpose: `RuleBased` has no `appliesTo` property in `packages/shared`
   * today, and inventing one here would be rejected by server validation and would show up
   * as a phantom entry in the release diff. PLAN-v2 phase 2 adds `appliesTo`/`countScope` in
   * the shared-contract commit; until then this only narrows the field choices, and is NOT
   * written into the document. Keyed `${actionRow}:${conditionRow}`.
   */
  const [rowSources, setRowSources] = useState<Record<string, SourceId[]>>({});
  /**
   * Wave 7 long windows — the unit each measure's window is typed in, keyed `${row}.${measure}`.
   * Editor-local: the document stores `windowSeconds` only, and the unit shown is re-derived
   * from it (`windowUnitFor`) whenever the pick no longer fits — a preset, a canvas edit.
   */
  const [windowUnits, setWindowUnits] = useState<Record<string, WindowUnit>>({});
  /** The feeder payload in force (the draft's, or the built-in one) — what the sources really send. */
  const feederConfig = useFeederConfig();
  /** Wave 6 — the set's custom fields join the field lists below (`fieldsForSources`), history ones excepted. */
  useCustomFields();
  /**
   * Which editor is in front. Both views edit the SAME `rule` object above — the segmented
   * control changes nothing about the rule, only how it is drawn, and an edit in either one
   * lands in the same place. Sentence is the default: it is the view every existing query,
   * test and screenshot expects, and it costs no download.
   */
  const [view, setView] = useState<'sentence' | 'blocks'>('sentence');
  /**
   * The canvas is mounted the first time Blocks is opened and then stays mounted, hidden.
   * Injecting Blockly again on every tab switch would lose the viewport and re-download
   * nothing useful; `visible` tells it to re-measure instead (`Blockly.svgResize`).
   */
  const [blocksMounted, setBlocksMounted] = useState(false);
  /**
   * UX review P2-4: Blockly could not be loaded. The editor goes back to the Sentence view, says
   * so once, and offers "Try again" — which mounts the canvases afresh (`blocksAttempt` is in
   * their key).
   */
  const [blocksFailed, setBlocksFailed] = useState(false);
  const [blocksAttempt, setBlocksAttempt] = useState(0);
  const onBlocksLoadError = () => {
    setBlocksFailed(true);
    setView('sentence');
  };
  /** The in-editor dry run panel. It runs the rule as edited and writes nothing. */
  const [dryRunOpen, setDryRunOpen] = useState(false);
  /** The Master lists a set condition may name (R7) — `null` while not known; the server decides then. */
  const masters = useMasterLists(settings);
  // 2c rule sets — the sets a `set_fired` measure may count (the screen under the editor has loaded them)
  const { list: setRegistry } = useRuleSets(settings, { fetch: false });
  const firedSets = setRegistry ? liveSets(setRegistry.sets) : [];
  /** A new `set_fired` measure counts this set's own fires until another is chosen. */
  const defaultFiredSet = ruleSet?.ruleSetId ?? firedSets[0]?.id;

  useEffect(() => {
    setRule(openRule(target));
    setRowSources({});
    setWindowUnits({});
    setView('sentence');
    setDryRunOpen(false);
  }, [target]);

  const rb = kind === 'ruleBased' ? (rule as RuleBased) : null;
  const pb = kind === 'patternBased' ? (rule as PatternBased) : null;


  // The problems' wording follows the language: `problemText` reads the catalogue at render.
  useLocale();

  /** What Apply saves and what the dry run posts: row 1 mirrored, `requires` derived. */
  const finalRule = useMemo(() => savedForm(kind, rule), [kind, rule]);
  /**
   * The rule as it opened, in the same saved form — so an untouched rule (whose stored form may
   * differ from what the editor writes, e.g. a derived `requires`) is not taken for an edit.
   * Editor-only state (the source buttons, the view) is not part of it.
   */
  const openedAs = useMemo(() => JSON.stringify(savedForm(target.kind, openRule(target))), [target]);

  /**
   * Cancel, ✕, Escape and the scrim: with edits not applied, ask first (QA-R-01) — a canvas edit
   * still on its debounce counts. "Keep editing" (or Escape on the question) leaves the editor open.
   */
  const requestClose = () => {
    if (readOnly) return onClose();
    flushCanvases();
    if (JSON.stringify(savedForm(kind, ruleRef.current)) !== openedAs) setAsk('discard');
    else onClose();
  };

  /** Apply: whatever a canvas still holds goes in first (QA-R-14), then the rule as it now is. */
  const apply = () => {
    flushCanvases();
    onSave(kind, savedForm(kind, ruleRef.current), target.rule.id);
  };

  /**
   * The engine checks the rule where it will live: the draft with this rule dropped in (at the
   * place Apply will put it). 400 ms after the last edit, or from "Check rules"; a Viewer never.
   * Only this rule's problems are shown — another rule's are the release dialog's business.
   */
  const candidate = useMemo(
    () => (ruleSet ? upsertRuleIn(ruleSet, kind, finalRule, target.rule.id) : null),
    [ruleSet, kind, finalRule, target.rule.id],
  );
  const check = useRuleCheck(settings, candidate, { enabled: !readOnly });
  const problems = useMemo(() => {
    if (!ruleSet || !check.result) return [];
    const index = upsertedIndex(ruleSet, kind, finalRule, target.rule.id);
    return withoutMirrorRepeats(problemsOfRule(check.result.problems, kind, index, finalRule.id));
  }, [ruleSet, check.result, kind, finalRule, target.rule.id]);
  /** Known bad: the engine's answer about exactly this rule found problems. Unchecked is not bad. */
  const blocked = check.current && problems.length > 0;

  const patch = (p: Partial<RuleBased & PatternBased>) => setRule((prev) => ({ ...prev, ...p }) as RuleBased | PatternBased);

  // ---- action rows (rule-based) ----------------------------------------------------
  const rows: RuleBranch[] = rb ? branchesOf(rb) : [];

  /**
   * Every row edit goes through `withBranches`, so the rule-level `action`/`clauses` always
   * mirror row 1 (what the engine's validation asks for). The rule's `points` is never touched:
   * there is no scoring (product owner, 30 Sep 2026), so whatever the document holds is saved
   * as it is. Each row's structure is its own: an edit to one row never reaches another.
   */
  const updateRows = (fn: (current: RuleBranch[]) => RuleBranch[]) =>
    setRule((prev) => {
      const before = prev as RuleBased;
      return withBranches(before, fn(branchesOf(before)));
    });

  const setRow = (k: number, change: Partial<RuleBranch>) =>
    updateRows((current) => current.map((row, i) => (i === k ? { ...row, ...change } : row)));

  /**
   * A new action row starts from one condition the analyst finishes — `field > 0` on a number
   * field no row uses yet — rather than a copy of row 1, which the engine at once reports as the
   * same conditions as row 1 and which blocked Apply before anything was typed (QA-R-12). When
   * every such field is taken, it falls back to a deep copy of row 1's conditions.
   */
  const addRow = () =>
    updateRows((current) => {
      const field = freshRowField(current, allowedFields);
      const clauses: Clause[] = field ? [{ field, op: 'gt', value: 0 }] : (current[0]?.clauses ?? NEW_BRANCH.clauses);
      return [...current, JSON.parse(JSON.stringify({ action: NEW_BRANCH.action, clauses })) as RuleBranch];
    });

  /** The rows below a removed row move up one, and their source choices move with them. */
  const shiftRowSources = (k: number) => setRowSources((prev) => shiftChains(prev, [], k) as Record<string, SourceId[]>);

  const removeRow = (k: number) => {
    updateRows((current) => current.filter((_, i) => i !== k));
    shiftRowSources(k);
  };

  // ---- action rows (pattern-based) -------------------------------------------------
  /**
   * The same rows for a pattern rule. Each row has its action and one or more measures —
   * each a whole aggregate condition (scope, event, aggregate, aggregate field, window,
   * threshold, filters) — joined by the row's AND/OR. Every edit goes through
   * `withPatternBranches`, so the rule-level fields mirror row 1's first measure.
   */
  const pbRows: PatternBranch[] = pb ? patternBranchesOf(pb) : [];

  const updatePbRows = (fn: (current: PatternBranch[]) => PatternBranch[]) =>
    setRule((prev) => {
      const before = prev as PatternBased;
      return withPatternBranches(before, fn(patternBranchesOf(before)));
    });

  const setPbRow = (k: number, change: Partial<PatternBranch>) =>
    updatePbRows((current) => current.map((row, i) => (i === k ? { ...row, ...change } : row)));

  const setMeasure = (k: number, j: number, change: Partial<PatternMeasure>) =>
    updatePbRows((current) =>
      current.map((row, i) => {
        if (i !== k) return row;
        const measures = row.measures.map((m, x) => {
          if (x !== j) return m;
          const next = { ...m, ...change };
          // a key set to `undefined` is removed, never stored as `undefined`
          for (const key of Object.keys(change) as (keyof PatternMeasure)[]) if (change[key] === undefined) delete next[key];
          return next;
        });
        // 2a expressions — renaming a counter renames every condition that uses it
        const before = row.measures[j]?.name;
        if (isExpressionRow(row) && change.name !== undefined && before !== undefined && change.name !== before) {
          return { ...row, measures, conditions: renameInConditions(row.conditions ?? [], before, change.name) };
        }
        return { ...row, measures };
      }),
    );

  /**
   * 2a expressions — switch action row `k` between a threshold row and an advanced
   * (expression) row. To advanced always works (`toExpressionRow` keeps what the row
   * evaluates); back only when exact (`canLeaveAdvanced`).
   */
  const setAdvanced = (k: number, on: boolean) =>
    updatePbRows((current) =>
      current.map((row, i) => {
        if (i !== k) return row;
        if (on) return isExpressionRow(row) ? row : toExpressionRow(row);
        return toThresholdRow(row) ?? row;
      }),
    );

  /** An advanced row can go back to thresholds only when that is exact. */
  const canLeaveAdvanced = (k: number): boolean => pbRows[k] !== undefined && toThresholdRow(pbRows[k]!) !== null;

  /**
   * "+ action" starts the new row as a copy of ALL of row 1 — every measure and the AND/OR
   * between them — as a deep copy with action APPROVE, so editing the new row (its scope,
   * event, window, filters…) never reaches back into row 1. Its first measure counts over a
   * window no row uses yet (`freshMeasure`): an exact copy is the same row as row 1, which the
   * engine reports at once and which blocked Apply before anything was typed (QA-R-12).
   */
  const addPbRow = () =>
    updatePbRows((current) => {
      const main = current[0] ?? patternBranchesOf(rule as PatternBased)[0]!;
      // 2a expressions — an expression row 1 is copied whole: counters, names and conditions
      if (isExpressionRow(main)) {
        const copy = JSON.parse(JSON.stringify(main)) as PatternBranch;
        copy.measures = copy.measures.map((m, j) => (j === 0 ? freshMeasure(m, current) : m));
        return [...current, { ...copy, action: NEW_BRANCH.action }];
      }
      const measures = main.measures.map((m, j) => (j === 0 ? freshMeasure(m, current) : copyMeasure(m)));
      const copy: PatternBranch = { action: NEW_BRANCH.action, measures };
      if (main.logic !== undefined) copy.logic = main.logic;
      return [...current, copy];
    });

  const removePbRow = (k: number) => {
    updatePbRows((current) => current.filter((_, i) => i !== k));
    shiftRowSources(k);
  };

  /** "+ aggregate" appends a deep copy of that row's LAST measure, to that row only. */
  const addMeasure = (k: number) =>
    updatePbRows((current) =>
      current.map((row, i) => {
        if (i !== k) return row;
        const copy = copyMeasure(row.measures[row.measures.length - 1]!);
        if (isExpressionRow(row)) copy.name = nextMeasureName(measureNames(row));
        return { ...row, measures: [...row.measures, copy] };
      }),
    );

  /** Removing measure j of a row removes it from that row only. */
  const removeMeasure = (k: number, j: number) => {
    updatePbRows((current) => current.map((row, i) => (i === k ? { ...row, measures: row.measures.filter((_, x) => x !== j) } : row)));
    setRowSources((prev) => shiftChains(prev, [k], j) as Record<string, SourceId[]>);
  };

  const problemFor = (path: string) => {
    const p = problems.find((x) => fieldPath(x) === path);
    return p ? problemText(p) : undefined;
  };
  /** The condition rows of one chain (`clauses`, `branches[1].clauses`, `…measures[0].filters`) that have problems. */
  const clauseNotes = (prefix: string) =>
    new Map([...chainProblems(problems, prefix)].map(([i, list]) => [i, list.map(problemText)] as const));

  /**
   * A row that collides with another (R6: the same conditions as a row above it, never reached
   * because a row above it matches first, or one condition twice) is marked on the row itself.
   */
  const rowProblems = (k: number) =>
    problems.filter(
      (p) =>
        (ROW_CONFLICT_CODES.has(p.code) && rowOf(p.path) === k) ||
        // 2a expressions — about the expression row itself (no logic, conditions, ≤ 8 counters)
        (EXPRESSION_ROW_CODES.has(p.code) && p.path === `branches[${k}]` && isExpressionRow(pbRows[k])),
    );
  /** 2a expressions — the engine's problems about anything inside `prefix[i]` (a condition, a counter), by index. */
  const nestedNotes = (prefix: string, exact = false) => {
    const out = new Map<number, string[]>();
    const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(`^${escaped}\\[(\\d+)\\]${exact ? '$' : '(\\.|$)'}`);
    for (const p of problems) {
      const m = p.path.match(re);
      if (!m) continue;
      // a problem the form already marks on its input (scope, window…) is not repeated under the card
      if (exact && fieldPath(p) !== p.path) continue;
      const i = Number(m[1]);
      out.set(i, [...(out.get(i) ?? []), problemText(p)]);
    }
    return out;
  };
  /**
   * Is this problem already written out, in words, next to what it is about in the view on
   * screen? Then the banner does not list it a second time (QA-R-07) — it still counts it.
   * Row conflicts are on their row in both views; condition, filter, counter and expression
   * notes only in the Sentence view (a canvas shows none of them).
   */
  const shownInline = (p: CheckedProblem): boolean => {
    const row = rowOf(p.path);
    if (row !== null && ROW_CONFLICT_CODES.has(p.code)) return true;
    if (row !== null && EXPRESSION_ROW_CODES.has(p.code) && p.path === `branches[${row}]` && isExpressionRow(pbRows[row])) return true;
    if (showBlocks) return false;
    if (rb) return (rows.length > 1 ? /^branches\[\d+\]\.clauses\[\d+\]$/ : /^clauses\[\d+\]$/).test(p.path);
    if (needsPatternBranches(pbRows) ? /^branches\[\d+\]\.measures\[\d+\]\.filters\[\d+\]$/.test(p.path) : /^filters\[\d+\]$/.test(p.path)) return true;
    if (row === null || !isExpressionRow(pbRows[row])) return false;
    if (/^branches\[\d+\]\.measures\[\d+\]$/.test(p.path)) return fieldPath(p) === p.path;
    return /^branches\[\d+\]\.conditions\[\d+\](\.|$)/.test(p.path);
  };
  const rowClass = (k: number) => (rowProblems(k).length > 0 ? 'branch invalid' : 'branch');
  const rowAlert = (k: number, testId = `branch-${k + 1}-problem`) => {
    const list = rowProblems(k);
    if (list.length === 0) return null;
    return (
      <div className="why-missing branch-problem" role="alert" data-testid={testId}>
        {/* One item per problem, as in the problems banner: a row can repeat a measure AND duplicate a row above. */}
        <ul>
          {list.map((p, i) => (
            <li key={i}>{problemText(p)}</li>
          ))}
        </ul>
      </div>
    );
  };

  // ---- source registry: which switches can actually run this rule ---------------
  /** Condition chains: each action row's clauses, or each pattern measure's filters. */
  const conditionChains: { key: string; clauses: Clause[] }[] = rb
    ? rows.map((row, k) => ({ key: `${k}`, clauses: row.clauses ?? [] }))
    : pbRows.flatMap((row, k) => row.measures.map((m, j) => ({ key: `${k}.${j}`, clauses: m.filters ?? [] })));

  /**
   * A row's selection, defaulting to every source that can carry its field, and always
   * filtered by what that field's sources really are — changing the field under a selection
   * must not leave a source selected that cannot send the new field.
   */
  const sourcesForRow = (chain: string, index: number, clause: Clause): SourceId[] => {
    const field = clause.field as FieldName;
    const chosen = rowSources[rowKey(chain, index)] ?? offeredFor(field, feederConfig);
    return chosen.filter((id) => sendsField(id, field, feederConfig));
  };

  const rowSourceLists: Record<string, SourceId[][]> = Object.fromEntries(
    conditionChains.map((chain) => [chain.key, chain.clauses.map((c, i) => sourcesForRow(chain.key, i, c))]),
  );
  const everyRowSources = Object.values(rowSourceLists).flat();

  /** Fields still offered in the dropdowns, given what the rows are switched on for. */
  const selectedSources = sourceIntersection(everyRowSources);
  const allowedFields = fieldsForSources(selectedSources, feederConfig);
  /** A pattern rule reads past events, which keep only the history fields — offer only those. */
  const historyFields = allowedFields.filter(isHistoryField);

  const setRowSource = (chain: string) => (index: number, next: SourceId[]) =>
    setRowSources((prev) => ({ ...prev, [rowKey(chain, index)]: next }));

  /**
   * A pattern canvas edit for action row `k` replaces that row's action and FIRST measure —
   * the canvas draws only measure 1 — and nothing else: its other measures, its AND/OR and
   * every other row stay. The measure is read off the canvas's one-measure rule, so a count
   * measure carries no `aggregateField` key.
   */
  const applyPbFromBlocks = (k: number, next: PatternBased) => {
    const first = patternBranchesOf({ ...next, branches: undefined })[0]!.measures[0]!;
    updatePbRows((current) => current.map((r, i) => (i === k ? { ...r, action: next.action, measures: [first, ...r.measures.slice(1)] } : r)));
  };

  const showBlocks = view === 'blocks';
  // 2c rule sets — a measure counting a set's fires has no block (the canvas cannot name the set)
  const countsSetFires =
    kind === 'patternBased' && patternBranchesOf(rule as PatternBased).some((row) => row.measures.some((m) => m.event === SET_FIRED));
  const openView = (next: 'sentence' | 'blocks') => {
    if (next === 'blocks') {
      setBlocksMounted(true);
      if (blocksFailed) {
        setBlocksFailed(false);
        setBlocksAttempt((n) => n + 1);
      }
    }
    setView(next);
  };

  /** The rule one action row's canvas shows: this rule, with only that row's action and clauses. */
  const rowRule = (row: RuleBranch): RuleBased => {
    const one: RuleBased = { ...(rule as RuleBased), action: row.action, clauses: row.clauses };
    delete one.branches;
    return one;
  };

  /** The same for a pattern row: this rule with that row's action and first measure. */
  const pbRowRule = (row: PatternBranch): PatternBased => patternRowRule(rule as PatternBased, row);

  /**
   * One canvas per action row, stacked. A canvas edit writes back ONLY its own row — for a
   * pattern rule, the row's action and first measure (a stacked row shows how many more
   * measures it has; those are edited in the Sentence view).
   */
  const blocksPanel =
    blocksMounted &&
    (rb ? (
      // `hidden` on a plain wrapper: `.stack` sets `display`, which would override it.
      <div hidden={!showBlocks}>
        <div className="stack">
          {rows.map((row, k) => {
            const one = rowRule(row);
            return (
              <div key={`${k}-${blocksAttempt}`} data-testid={`blocks-branch-${k + 1}`} className={rowProblems(k).length > 0 ? 'invalid' : undefined}>
                <div className="note row-wrap" style={{ margin: '0 0 4px' }}>
                  {t('branch.caption.label', { n: k + 1, action: row.action })}
                </div>
                <BlocksWorkspace
                  kind="rb"
                  rule={one}
                  allowedFields={allowedFields}
                  usedFields={fieldsUsedBy(one)}
                  visible={showBlocks}
                  readOnly={readOnly}
                  onLoadError={onBlocksLoadError}
                  registerFlush={registerFlush}
                  onChange={readOnly ? ignoreCanvas : (next) => setRow(k, { action: next.action, clauses: (next as RuleBased).clauses })}
                />
                {rowAlert(k, `blocks-branch-${k + 1}-problem`)}
              </div>
            );
          })}
        </div>
      </div>
    ) : (
      <div hidden={!showBlocks}>
        <div className="stack">
          {pbRows.map((row, k) => {
            if (isExpressionRow(row)) {
              // 2a expressions — the canvas cannot draw named counters and conditions: the row is
              // shown read-only here and never rewritten by a canvas.
              return (
                <div key={k} data-testid={`blocks-branch-${k + 1}`} className={rowProblems(k).length > 0 ? 'invalid' : undefined}>
                  <div className="note row-wrap" style={{ margin: '0 0 4px' }}>
                    {t('branch.caption.label', { n: k + 1, action: row.action })}
                  </div>
                  <div className="banner" data-testid={`blocks-branch-${k + 1}-expr`}>
                    <div>{t('expr.blocks.note', { n: k + 1 })}</div>
                    <div className="small">{expressionRowText(row)}</div>
                  </div>
                  {rowAlert(k, `blocks-branch-${k + 1}-problem`)}
                </div>
              );
            }
            const one = pbRowRule(row);
            return (
              <div key={`${k}-${blocksAttempt}`} data-testid={`blocks-branch-${k + 1}`} className={rowProblems(k).length > 0 ? 'invalid' : undefined}>
                <div className="note row-wrap" style={{ margin: '0 0 4px' }}>
                  {t('branch.caption.label', { n: k + 1, action: row.action })}
                  {row.measures.length > 1 && (
                    <span className="chip info" data-testid={`blocks-branch-${k + 1}-more`}>
                      {t('branch.measures.more.chip', { n: row.measures.length - 1 })}
                    </span>
                  )}
                </div>
                <BlocksWorkspace
                  kind="pb"
                  rule={one}
                  allowedFields={historyFields}
                  usedFields={fieldsUsedBy(one)}
                  visible={showBlocks}
                  readOnly={readOnly}
                  onLoadError={onBlocksLoadError}
                  registerFlush={registerFlush}
                  onChange={readOnly ? ignoreCanvas : (next) => applyPbFromBlocks(k, next as PatternBased)}
                />
                {rowAlert(k, `blocks-branch-${k + 1}-problem`)}
              </div>
            );
          })}
        </div>
      </div>
    ));


  const viewSwitch = (
    <>
      <div className="row-wrap" style={{ marginBottom: 2 }}>
        <div className="segbar" data-seg role="group" aria-label={t('editor.view.a11y')}>
          <button type="button" aria-pressed={!showBlocks} onClick={() => openView('sentence')}>
            {t('view.sentence.label')}
          </button>
          <button
            type="button"
            aria-pressed={showBlocks}
            // 2c rule sets — the blocks have no rule-set choice: a `set_fired` rule is edited in Sentence
            disabled={countsSetFires && !showBlocks}
            title={countsSetFires ? t('rulesets.blocks.off.tip') : undefined}
            onClick={() => openView('blocks')}
          >
            {t('view.blocks.label')}
          </button>
        </div>
      </div>
      {blocksFailed && (
        <div className="banner warn row-wrap" role="status" data-testid="blocks-failed">
          <span>{t('view.blocks.failed')}</span>
          <span className="spacer" />
          <button type="button" className="btn small" data-testid="blocks-retry" onClick={() => openView('blocks')}>
            {t('view.blocks.retry')}
          </button>
        </div>
      )}
    </>
  );

  /** The banner lists what is not already written next to its field or row. */
  const bannerProblems = problems.filter((p) => !shownInline(p));

  return (
    <>
    <Dialog
      wide
      title={t(`editor.${readOnly ? 'view' : target.isNew ? 'new' : 'edit'}.${kind === 'ruleBased' ? 'rb' : 'pb'}.title`)}
      a11yLabel={t('editor.dialog.a11y', { id: rule.id })}
      closeLabel={t('editor.close.a11y')}
      onClose={requestClose}
      head={
        <>
          <span className="mono muted">{rule.id}</span>
          {readOnly && (
            <span className="chip mute" data-testid="editor-readonly">
              {t('shell.readonly.suffix')}
            </span>
          )}
          {/* The caveat is a fact about the rule, so it stays visible as a mark; its full
              text is prose that belongs behind an `i`, not across the top of the form. */}
          {rule.warning && (
            <span className="chip warn" data-testid="rule-warning">
              ⚠ <Tip label={t('chip.has_caveat.label')}>{rule.warning}</Tip>
            </span>
          )}
          <span className="spacer" />
        </>
      }
      foot={
        <>
          {onDelete && !readOnly && !target.isNew && (
            <button type="button" className="btn danger" onClick={() => setAsk('delete')}>
              {t('editor.delete.action')}
            </button>
          )}
          <span className="spacer" />
          {/* Dry run is for whoever may change the draft — the BFF answers a Viewer 403. */}
          {/* Demo mode has no engine to run a dry run against (Wave 2-0), so it is not offered. */}
          {!readOnly && !DEMO && !ruleSet && <span className="note">{t('editor.dryrun.noDraft.note')}</span>}
          {!readOnly && !DEMO && (
            <button type="button" className="btn" aria-pressed={dryRunOpen} disabled={!ruleSet} onClick={() => setDryRunOpen((v) => !v)}>
              {t('editor.dryrun.action')}
            </button>
          )}
          {!readOnly && <CheckButton check={check} testid="editor-check-rules" />}
          {/* Read-only there is nothing to cancel and nothing to apply — only a way out. */}
          <button type="button" className="btn" onClick={requestClose}>
            {readOnly ? t('btn.close.label') : t('btn.cancel.label')}
          </button>
          {!readOnly && (
            <button type="button" className="btn primary" disabled={blocked} onClick={apply}>
              {target.isNew ? t('editor.add.action') : t('editor.apply.action')}
            </button>
          )}
        </>
      }
    >
      {/* ---- identity ------------------------------------------------- */}
      <div className="form-grid">
        <label className="field">
          {t('col.code.label')}
          <input
            type="text"
            value={rule.id}
            aria-label={t('editor.id.a11y')}
            disabled={readOnly}
            className={problemFor('id') ? 'invalid' : ''}
            onChange={(e) => patch({ id: e.target.value.toUpperCase() })}
          />
        </label>
        <label className="field" style={{ gridColumn: 'span 2' }}>
          {t('col.name.label')}
          <input
            type="text"
            aria-label={t('col.rule_name.label')}
            disabled={readOnly}
            value={rule.name}
            className={problemFor('name') ? 'invalid' : ''}
            placeholder={t('form.ruleName.hint')}
            onChange={(e) => patch({ name: e.target.value })}
          />
        </label>
        <label className="field">
          {t('form.priority.label')}
          <select aria-label={t('col.priority.label')} disabled={readOnly} value={rule.priority} onChange={(e) => patch({ priority: e.target.value as RuleBased['priority'] })}>
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="row-wrap">
        <label className="check">
          <input className="toggle" type="checkbox" role="switch" aria-label={t('col.enabled.label')} disabled={readOnly} checked={rule.enabled} onChange={(e) => patch({ enabled: e.target.checked })} />
          {rule.enabled ? t('form.status.on') : t('form.status.off')}
        </label>
      </div>

      {/* ---- rule-based: one row per action -------------------------- */}
      {rb && (
        <>
          {viewSwitch}
          {!showBlocks && (
            <div>
              <div className="branches">
                {rows.map((row, k) => (
                  <div className={rowClass(k)} key={k} data-testid={`branch-${k + 1}`}>
                    <div className="branch-cell">
                      <select
                        className={`branch-act ${ACTION_CLASS[row.action] ?? ''}`}
                        aria-label={t('branch.action.a11y', { n: k + 1 })}
                        disabled={readOnly}
                        value={row.action}
                        onChange={(e) => setRow(k, { action: e.target.value as RuleAction })}
                      >
                        {ACTIONS.map((a) => (
                          <option key={a} value={a}>
                            {a}
                          </option>
                        ))}
                      </select>
                    </div>
                    <ClauseBuilder
                      showPreview={false}
                      idPrefix={`rb-b${k + 1}-clause`}
                      clauses={row.clauses ?? []}
                      allowedFields={allowedFields}
                      rowSources={rowSourceLists[`${k}`]}
                      onRowSourcesChange={setRowSource(`${k}`)}
                      readOnly={readOnly}
                      masters={masters}
                      notes={clauseNotes(rows.length > 1 ? `branches[${k}].clauses` : 'clauses')}
                      onChange={(clauses: Clause[]) => setRow(k, { clauses })}
                    />
                    <div className="branch-del">
                      {!readOnly && rows.length > 1 && (
                        <button
                          type="button"
                          className="btn small ghost rowdel"
                          aria-label={t('branch.remove.a11y', { n: k + 1 })}
                          onClick={() => removeRow(k)}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                    {rowAlert(k)}
                  </div>
                ))}
              </div>
              {!readOnly && (
                <button type="button" className="pill add branch-add" onClick={addRow}>
                  {t('branch.add.label')}
                </button>
              )}
            </div>
          )}
          {blocksPanel}
        </>
      )}

      {/* ---- pattern-based: one row per action, each with its own full pattern ---- */}
      {pb && (
        <>
          {viewSwitch}
          {!showBlocks && (
            <div>
              <div className="branches">
                {pbRows.map((row, k) => {
                  const n = k + 1;
                  const branched = needsPatternBranches(pbRows);
                  // 2a expressions — an expression row has named counters and conditions
                  const expr = isExpressionRow(row);
                  const names = measureNames(row);
                  const leaveOk = expr && canLeaveAdvanced(k);
                  return (
                    <div className={rowClass(k)} key={k} data-testid={`branch-${n}`}>
                      <div className="branch-cell">
                        <select
                          className={`branch-act ${ACTION_CLASS[row.action] ?? ''}`}
                          aria-label={t('branch.action.a11y', { n })}
                          disabled={readOnly}
                          value={row.action}
                          onChange={(e) => setPbRow(k, { action: e.target.value as RuleAction })}
                        >
                          {ACTIONS.map((a) => (
                            <option key={a} value={a}>
                              {a}
                            </option>
                          ))}
                        </select>
                        {/* 2a expressions — threshold row ⇄ advanced condition */}
                        <label className="check small" title={expr && !leaveOk ? t('expr.mode.back.tip') : t('expr.mode.tip')}>
                          <input
                            className="toggle"
                            type="checkbox"
                            role="switch"
                            aria-label={t('expr.mode.a11y', { n })}
                            disabled={readOnly || (expr && !leaveOk)}
                            checked={expr}
                            onChange={(e) => setAdvanced(k, e.target.checked)}
                          />
                          {t('expr.mode.label')}
                        </label>
                      </div>
                      <div className="measures">
                        {row.measures.map((m, j) => {
                          const ids = { n, m: j + 1 };
                          const at = branched ? `branches[${k}].measures[${j}].` : '';
                          const setAggregate = (aggregate: PatternMeasure['aggregate']) => {
                            if (!expr) {
                              if (aggregate === 'count') setMeasure(k, j, { aggregate, aggregateField: undefined });
                              else setMeasure(k, j, { aggregate, aggregateField: m.aggregateField ?? (aggregate === 'sum' ? 'amount_thb' : 'card_id') });
                              return;
                            }
                            // 2a expressions — what each aggregate needs, set as it is chosen
                            const change: Partial<PatternMeasure> = { aggregate };
                            if (!FIELD_AGGREGATES.has(aggregate)) change.aggregateField = undefined;
                            else if (NUMERIC_AGGREGATES.has(aggregate)) change.aggregateField = m.aggregateField && FIELD_KINDS[m.aggregateField as FieldName] === 'number' ? m.aggregateField : 'amount_thb';
                            else change.aggregateField = m.aggregateField ?? (aggregate === 'distinct' ? 'card_id' : 'merchant_country_num');
                            if (HISTORY_ONLY_AGGREGATES.has(aggregate)) change.includeCurrent = false;
                            if (aggregate === 'streak') change.event = 'decline';
                            setMeasure(k, j, change);
                          };
                          const cardNotes = expr ? nestedNotes(`branches[${k}].measures`, true).get(j) ?? [] : [];
                          // Wave 7 long windows — the window's unit, and what a window over 24 h means
                          const unitKey = `${k}.${j}`;
                          const unit = windowUnitFor(m.windowSeconds, windowUnits[unitKey]);
                          const long = isLongWindow(m.windowSeconds);
                          const days = windowDays(m.windowSeconds);
                          const setWindow = (next: { seconds: number; unit: WindowUnit }) => {
                            setWindowUnits((prev) => ({ ...prev, [unitKey]: next.unit }));
                            setMeasure(k, j, { windowSeconds: next.seconds });
                          };
                          return (
                            <Fragment key={j}>
                              {j > 0 && !expr && (
                                <div className="measure-join">
                                  <select
                                    className="pill logic"
                                    aria-label={t('branch.measures.join.a11y', { n })}
                                    disabled={readOnly}
                                    value={row.logic ?? 'AND'}
                                    onChange={(e) => setPbRow(k, { logic: e.target.value as 'AND' | 'OR' })}
                                  >
                                    <option value="AND">AND</option>
                                    <option value="OR">OR</option>
                                  </select>
                                </div>
                              )}
                              <div className={`measure${cardNotes.length > 0 ? ' invalid' : ''}`} data-testid={`pb-b${n}-m${j + 1}`}>
                                {expr && (
                                  <div className="branch-count">
                                    <label className="field">
                                      {t('expr.measure.name.label')}
                                      <input
                                        type="text"
                                        className="f-val"
                                        aria-label={t('expr.measure.name.a11y', ids)}
                                        disabled={readOnly}
                                        value={m.name ?? ''}
                                        onChange={(e) => setMeasure(k, j, { name: e.target.value })}
                                      />
                                    </label>
                                    <label className="field">
                                      {t('expr.measure.calendar.label')}
                                      <select
                                        aria-label={t('expr.measure.calendar.a11y', ids)}
                                        disabled={readOnly}
                                        value={m.calendar ?? ''}
                                        onChange={(e) => {
                                          const cal = e.target.value as '' | 'hour' | 'day';
                                          if (cal === '') setMeasure(k, j, { calendar: undefined });
                                          else setMeasure(k, j, { calendar: cal, windowSeconds: cal === 'hour' ? 3600 : 86400 });
                                        }}
                                      >
                                        <option value="">{t('expr.calendar.rolling')}</option>
                                        <option value="hour">{t('expr.calendar.hour')}</option>
                                        <option value="day">{t('expr.calendar.day')}</option>
                                      </select>
                                    </label>
                                    <label className="check small" title={t('expr.measure.current.tip')}>
                                      <input
                                        type="checkbox"
                                        aria-label={t('expr.measure.current.a11y', ids)}
                                        disabled={readOnly || HISTORY_ONLY_AGGREGATES.has(m.aggregate)}
                                        checked={m.includeCurrent !== false}
                                        onChange={(e) => setMeasure(k, j, { includeCurrent: e.target.checked ? undefined : false })}
                                      />
                                      {t('expr.measure.current.label')}
                                    </label>
                                  </div>
                                )}
                                <div className="branch-count">
                                  <label className="field">
                                    {t('branch.scope.label')}
                                    <select
                                      aria-label={t('branch.scope.a11y', ids)}
                                      disabled={readOnly}
                                      className={problemFor(`${at}scope`) ? 'invalid' : ''}
                                      value={m.scope}
                                      onChange={(e) => setMeasure(k, j, { scope: e.target.value as PatternMeasure['scope'] })}
                                    >
                                      {(expr ? EXPR_SCOPES : SCOPES).map((s) => (
                                        <option key={s} value={s}>
                                          {s}
                                        </option>
                                      ))}
                                    </select>
                                  </label>
                                  <label className="field">
                                    {t('branch.event.label')}
                                    <select
                                      aria-label={t('branch.event.a11y', ids)}
                                      disabled={readOnly}
                                      className={problemFor(`${at}event`) ? 'invalid' : ''}
                                      value={m.event}
                                      onChange={(e) => {
                                        const event = e.target.value as PatternMeasure['event'];
                                        // 2c rule sets — `set_fired` names its set; any other event names none
                                        if (event === SET_FIRED) setMeasure(k, j, { event, ruleSetId: m.ruleSetId ?? defaultFiredSet });
                                        else setMeasure(k, j, { event, ruleSetId: undefined });
                                      }}
                                    >
                                      {EVENTS.map((s) => (
                                        <option key={s} value={s}>
                                          {t(`event.${s}.label`)}
                                        </option>
                                      ))}
                                      {/* 2c rule sets — offered once the sets are known (or already chosen) */}
                                      {(firedSets.length > 0 || m.event === SET_FIRED) && <option value={SET_FIRED}>{t('event.set_fired.label')}</option>}
                                    </select>
                                  </label>
                                  {/* 2c rule sets — which set's fires are counted */}
                                  {m.event === SET_FIRED && (
                                    <label className="field">
                                      {t('rulesets.measure.set.label')}
                                      <select
                                        aria-label={t('rulesets.measure.set.a11y', ids)}
                                        disabled={readOnly}
                                        className={problemFor(`${at}ruleSetId`) ? 'invalid' : ''}
                                        value={m.ruleSetId ?? ''}
                                        onChange={(e) => setMeasure(k, j, { ruleSetId: e.target.value || undefined })}
                                      >
                                        <option value="">{t('form.choose.option')}</option>
                                        {firedSets.map((s) => (
                                          <option key={s.id} value={s.id}>
                                            {setLabel(s)}
                                          </option>
                                        ))}
                                        {m.ruleSetId && !firedSets.some((s) => s.id === m.ruleSetId) && <option value={m.ruleSetId}>{m.ruleSetId}</option>}
                                      </select>
                                    </label>
                                  )}
                                  <label className="field">
                                    {t('branch.aggregate.label')}
                                    <select
                                      aria-label={t('branch.aggregate.a11y', ids)}
                                      disabled={readOnly}
                                      className={problemFor(`${at}aggregate`) ? 'invalid' : ''}
                                      value={m.aggregate}
                                      onChange={(e) => setAggregate(e.target.value as PatternMeasure['aggregate'])}
                                    >
                                      {(expr ? EXPR_AGGREGATES : AGGREGATES).map((s) => {
                                        // Wave 7 — a long window keeps per-day totals: no latest value, gap or streak
                                        const off = long && LONG_OFF_AGGREGATES.has(s);
                                        return (
                                          <option key={s} value={s} disabled={off} title={off ? t('branch.aggregate.long.off.tip') : undefined}>
                                            {aggregateLabel(s)}
                                          </option>
                                        );
                                      })}
                                    </select>
                                  </label>
                                  {(m.aggregate === 'sum' || m.aggregate === 'distinct' || (expr && FIELD_AGGREGATES.has(m.aggregate))) && (
                                    <label className="field">
                                      {t('branch.aggregateField.label')}
                                      <select
                                        aria-label={t('branch.aggregateField.a11y', ids)}
                                        disabled={readOnly}
                                        className={problemFor(`${at}aggregateField`) ? 'invalid' : ''}
                                        value={String(m.aggregateField ?? '')}
                                        onChange={(e) => setMeasure(k, j, { aggregateField: (e.target.value || undefined) as FieldName | undefined })}
                                      >
                                        <option value="">{t('form.choose.option')}</option>
                                        {FIELD_GROUPS.map((g) => {
                                          // history fields only (the row's own field stays, so an old rule still shows it); a group left empty is dropped
                                          const offered = g.fields
                                            .filter((f) => f === m.aggregateField || isHistoryField(f))
                                            .filter((f) => (NUMERIC_AGGREGATES.has(m.aggregate) ? FIELD_KINDS[f] === 'number' : true));
                                          if (offered.length === 0) return null;
                                          return (
                                            <optgroup key={g.group} label={t(g.labelKey)}>
                                              {offered.map((f) => (
                                                <option key={f} value={f}>
                                                  {f}
                                                </option>
                                              ))}
                                            </optgroup>
                                          );
                                        })}
                                      </select>
                                    </label>
                                  )}
                                  {/* A number and its unit (Wave 7): seconds / minutes / hours up to 24 h, then days, months, a year. */}
                                  <div className="field">
                                    <span>
                                      {t('branch.window.label', { unit: t(UNIT_LABEL[unit]) })} <Tip>{t('editor.window.tip')}</Tip>
                                    </span>
                                    <span className="window-input">
                                      {/* Unit first, then the number from a list (owner, 30 Sep). */}
                                      <select
                                        aria-label={t('branch.window.unit.a11y', ids)}
                                        disabled={readOnly || m.calendar !== undefined}
                                        value={unit}
                                        onChange={(e) => {
                                          const to = Number(e.target.value) as WindowUnit;
                                          setWindow({ seconds: UNIT_DEFAULT[to] * to, unit: to });
                                        }}
                                      >
                                        {WINDOW_UNITS.map((u) => (
                                          <option key={u} value={u}>
                                            {t(UNIT_LABEL[u])}
                                          </option>
                                        ))}
                                      </select>
                                      <select
                                        className={`window-num${problemFor(`${at}windowSeconds`) ? ' invalid' : ''}`}
                                        aria-label={t('branch.window.a11y', ids)}
                                        disabled={readOnly || m.calendar !== undefined}
                                        value={String(windowInputValue(m.windowSeconds, unit))}
                                        onChange={(e) => setWindow({ seconds: Number(e.target.value) * unit, unit })}
                                      >
                                        {/* no window set (an older document): say so rather than show the first number as if it were the rule's */}
                                        {!Number.isFinite(m.windowSeconds) && (
                                          <option value="" disabled>
                                            —
                                          </option>
                                        )}
                                        {windowChoices(unit, m.windowSeconds).map((v) => (
                                          <option key={v} value={v}>
                                            {v}
                                          </option>
                                        ))}
                                      </select>
                                    </span>
                                  </div>
                                </div>
                                <div className="branch-win row-wrap">
                                  <span className="small muted">= {formatWindow(m.windowSeconds)}</span>
                                  {!readOnly && m.calendar === undefined && (
                                    // The window in force is the pressed one (UX review P2-3); a picked value no preset matches presses none.
                                    <span className="segbar" role="group" aria-label={t('branch.window.presets.a11y', ids)}>
                                      {WINDOW_PRESETS.map((seconds) => (
                                        <button
                                          key={seconds}
                                          type="button"
                                          aria-pressed={m.windowSeconds === seconds}
                                          onClick={() => setWindow({ seconds, unit: presetUnit(seconds) })}
                                        >
                                          {formatWindow(seconds)}
                                        </button>
                                      ))}
                                    </span>
                                  )}
                                  <span className="spacer" />
                                  {!readOnly && row.measures.length > 1 && (
                                    <button
                                      type="button"
                                      className="btn small ghost rowdel"
                                      aria-label={t('branch.measure.remove.a11y', ids)}
                                      onClick={() => removeMeasure(k, j)}
                                    >
                                      ✕
                                    </button>
                                  )}
                                </div>
                                {long && days !== null && (
                                  // Wave 7 — what a long window counts, and from when
                                  <div className="branch-win window-notes small muted" role="note" data-testid={`pb-b${n}-m${j + 1}-window-notes`}>
                                    <span data-testid={`pb-b${n}-m${j + 1}-window-days`}>{t('branch.window.long.days', { n: days })}</span>
                                    <span data-testid={`pb-b${n}-m${j + 1}-window-release`}>{t('branch.window.long.release')}</span>
                                    {m.aggregate === 'distinct' && (
                                      <span className="chip info" data-testid={`pb-b${n}-m${j + 1}-window-approx`}>
                                        {t('longWindow.approximate')}
                                      </span>
                                    )}
                                  </div>
                                )}
                                {!expr && (
                                <label className="branch-thr small">
                                  {t('branch.threshold.label')}
                                  <input
                                    type="number"
                                    className={`num-input ${problemFor(`${at}threshold`) ? 'invalid' : ''}`}
                                    aria-label={t('branch.threshold.a11y', ids)}
                                    disabled={readOnly}
                                    value={Number.isFinite(m.threshold) ? m.threshold : ''}
                                    onChange={(e) => setMeasure(k, j, { threshold: e.target.value === '' ? Number.NaN : Number(e.target.value) })}
                                  />
                                  <Tip>{t('branch.filters.tip')}</Tip>
                                </label>
                                )}
                                <ClauseBuilder
                                  showPreview={false}
                                  idPrefix={`pb-b${n}-m${j + 1}-filter`}
                                  showLogic={false}
                                  clauses={m.filters ?? []}
                                  allowedFields={historyFields}
                                  rowSources={rowSourceLists[`${k}.${j}`]}
                                  onRowSourcesChange={setRowSource(`${k}.${j}`)}
                                  readOnly={readOnly}
                                  masters={masters}
                                  notes={clauseNotes(`${at}filters`)}
                                  onChange={(filters: Clause[]) => setMeasure(k, j, { filters })}
                                />
                                {cardNotes.length > 0 && (
                                  <div className="why-missing" role="alert" data-testid={`pb-b${n}-m${j + 1}-problem`}>
                                    <ul>
                                      {cardNotes.map((text, x) => (
                                        <li key={x}>{text}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            </Fragment>
                          );
                        })}
                        {!readOnly && (!expr || row.measures.length < MAX_EXPR_MEASURES) && (
                          <button type="button" className="pill add measure-add" onClick={() => addMeasure(k)}>
                            {expr ? t('expr.measure.add') : t('branch.measure.add.label')}
                          </button>
                        )}
                        {expr && (
                          <>
                            <ExprConditions
                              conditions={row.conditions ?? []}
                              names={names}
                              n={n}
                              readOnly={readOnly}
                              allowedFields={allowedFields}
                              notes={nestedNotes(`branches[${k}].conditions`)}
                              onChange={(conditions) => setPbRow(k, { conditions })}
                            />
                            <div className="preview" data-testid={`pb-b${n}-readback`}>
                              <span className="small muted">{t('expr.readback.label')} </span>
                              {expressionRowText(row)}
                            </div>
                          </>
                        )}
                      </div>
                      <div className="branch-del">
                        {!readOnly && pbRows.length > 1 && (
                          <button
                            type="button"
                            className="btn small ghost rowdel"
                            aria-label={t('branch.remove.a11y', { n })}
                            onClick={() => removePbRow(k)}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                      {rowAlert(k)}
                    </div>
                  );
                })}
              </div>
              {!readOnly && (
                <button type="button" className="pill add branch-add" onClick={addPbRow}>
                  {t('branch.add.label')}
                </button>
              )}
            </div>
          )}
          {blocksPanel}
        </>
      )}

      {/* ---- rationale ------------------------------------------------ */}
      <label className="field">
        {t('form.rationale.label')}
        <textarea aria-label={t('editor.rationale.a11y')} disabled={readOnly} value={rule.rationale ?? ''} onChange={(e) => patch({ rationale: e.target.value })} />
      </label>

      {problems.length > 0 && (
        <div className="banner error" role="alert" data-testid="editor-problems">
          <strong>{t(problems.length === 1 ? 'editor.problems.one' : 'editor.problems.many', { n: problems.length })}</strong>
          {/* Where each one is, in words; what is already said on its row is not said twice. */}
          {bannerProblems.length > 0 && <ProblemList problems={bannerProblems} className="" pathOf={fieldPath} />}
        </div>
      )}
      {!readOnly && <CheckStatusLine check={check} testid="editor-check-status" />}

      {dryRunOpen && !readOnly && ruleSet && <InlineDryRun settings={settings} ruleSet={ruleSet} kind={kind} rule={finalRule} />}
    </Dialog>
    {ask === 'discard' && (
      <ConfirmDialog
        testid="editor-discard"
        title={t('editor.discard.title', { id: target.rule.id || rule.id })}
        confirmLabel={t('editor.discard.confirm')}
        cancelLabel={t('editor.discard.keep')}
        onCancel={() => setAsk(null)}
        onConfirm={() => {
          setAsk(null);
          onClose();
        }}
      >
        <p className="small">{t('editor.discard.body')}</p>
      </ConfirmDialog>
    )}
    {ask === 'delete' && onDelete && (
      <ConfirmDialog
        testid="editor-delete"
        title={t('editor.delete.title', { id: target.rule.id })}
        confirmLabel={t('editor.delete.action')}
        cancelLabel={t('btn.cancel.label')}
        onCancel={() => setAsk(null)}
        onConfirm={() => {
          setAsk(null);
          onDelete(kind, target.rule.id);
        }}
      >
        <p className="small">{t('editor.delete.body', { id: target.rule.id })}</p>
      </ConfirmDialog>
    )}
    </>
  );
}
